/**
 * Partida online (item 6.1), em `#online`: entra na sala do servidor com o seu atleta, assume a vaga de um bot e
 * joga com quem mais estiver na sala. O servidor decide tudo; a cena desenha as fotos que chegam.
 * `#online?servidor=ws://…` escolhe outro servidor.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { POSITIONS, type Position } from "@besenliga/shared";
import { browserStore, loadRoster } from "../creator/roster";
import { Banner, ControlsHelp, Feed, Minimap, PilotPanel, Scoreboard, useFlightView } from "../flight/Hud";
import { pilotStats } from "../flight/lineup";
import { pilotFrom, type Pilot } from "../flight/pilot";
import { DEFAULT_CLUBS, type ViewOptions } from "../flight/view";
import { Connection, serverUrl } from "./connection";

const ROLES: Position[] = ["artilheiro", "batedor", "cacador", "goleiro"];

export function OnlineScreen() {
  const roster = useMemo(() => loadRoster(browserStore()), []);
  const [athleteId, setAthleteId] = useState<string | null>(roster[0]?.id ?? null);
  const [role, setRole] = useState<Position>(roster[0]?.position ?? "artilheiro");
  const [conn, setConn] = useState<Connection | null>(null);
  const [status, setStatus] = useState<"escolha" | "conectando" | "erro">("escolha");
  const [error, setError] = useState("");
  const [, force] = useState(0);
  const url = useMemo(serverUrl, []);
  const saved = roster.find((a) => a.id === athleteId);
  const pilot = useMemo(() => pilotFrom(saved, role), [saved, role]);

  useEffect(() => () => void conn?.leave(), [conn]);

  const enter = async () => {
    setStatus("conectando");
    try {
      const { posicao: _p, ...look } = pilot.spec;
      void _p;
      const c = await Connection.join(url, {
        name: pilot.name,
        role,
        look,
        stats: { ...pilotStats(pilot.spec) },
      });
      c.onChange = () => force((n) => n + 1);
      // Espera a vaga e a primeira foto.
      const end = Date.now() + 8000;
      while (!c.ready && Date.now() < end) await new Promise((r) => setTimeout(r, 50));
      if (!c.ready) throw new Error("O servidor não respondeu.");
      setConn(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus("erro");
    }
  };

  if (conn && !conn.isClosed) return <OnlineGame conn={conn} pilot={pilot} onLeave={() => setConn(null)} />;

  return (
    <main className="menu-screen">
      <section className="menu-card wide" aria-labelledby="online-titulo">
        <header className="menu-head">
          <a href="#" className="ghost">
            Menu
          </a>
          <h1 id="online-titulo">Partida online</h1>
        </header>
        <p>
          Você entra na sala do servidor e assume a vaga de um bot; quem mais entrar joga com você ou contra
          você. As vagas vazias continuam com bots médios.
        </p>
        {roster.length > 0 && (
          <fieldset className="menu-group">
            <legend>Seu atleta</legend>
            <div className="menu-options">
              {roster.map((a) => (
                <label key={a.id} className="menu-option">
                  <input
                    type="radio"
                    name="atleta"
                    checked={athleteId === a.id}
                    onChange={() => {
                      setAthleteId(a.id);
                      setRole(a.position);
                    }}
                  />
                  <span>
                    <strong>{a.name}</strong> · {POSITIONS[a.position].label}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <fieldset className="menu-group">
          <legend>Posição que você quer</legend>
          <div className="segmented" role="group" aria-label="Posição">
            {ROLES.map((r) => (
              <button key={r} type="button" aria-pressed={role === r} onClick={() => setRole(r)}>
                {POSITIONS[r].label}
              </button>
            ))}
          </div>
        </fieldset>
        <p className="training-best">Servidor: {url}</p>
        {status === "erro" && (
          <p className="career-warn" role="alert">
            Não deu para entrar: {error}
          </p>
        )}
        <div className="menu-actions row">
          <button type="button" className="primary" onClick={enter} disabled={status === "conectando"}>
            {status === "conectando" ? "Conectando…" : `Entrar com ${pilot.name}`}
          </button>
        </div>
      </section>
    </main>
  );
}

function OnlineGame({ conn, pilot, onLeave }: { conn: Connection; pilot: Pilot; onLeave: () => void }) {
  const holderRef = useRef<HTMLDivElement>(null);
  const clubs = DEFAULT_CLUBS;
  const kits = useMemo(() => ({ casa: clubs.casa.kit, fora: clubs.fora.kit }), [clubs]);
  const mine = conn.roster.find((r) => r.id === conn.you);
  const role = mine?.role ?? pilot.role;
  const seatPilot = useMemo(() => ({ ...pilot, role }), [pilot, role]);
  const opts: ViewOptions = useMemo(() => ({ mode: "parados", clubs, remote: conn }), [clubs, conn]);
  const { state, hud, view } = useFlightView(holderRef, seatPilot, opts);
  const humans = conn.roster.filter((r) => r.human);
  const ended = hud?.match?.phase === "fim";

  return (
    <div className="stadium-screen flight-screen" data-state={state}>
      <div ref={holderRef} className="stadium-holder" />
      <header className="stadium-bar">
        <button
          type="button"
          className="ghost"
          onClick={() => {
            void conn.leave();
            onLeave();
          }}
        >
          Sair
        </button>
        <h1>Online</h1>
      </header>
      {hud && <Scoreboard hud={hud} clubs={clubs} />}
      <Minimap view={view} kits={kits} />
      {hud && <Feed hud={hud} />}
      {hud && (
        <Banner hud={hud} clubs={clubs}>
          {ended && <span>Outra partida começa em instantes nesta sala.</span>}
        </Banner>
      )}
      <aside className="online-players" aria-label="Pessoas na partida">
        <NetLine conn={conn} />
        <p>Na partida ({humans.length})</p>
        <ul>
          {humans.map((r) => (
            <li key={r.id} className={r.id === conn.you ? "you" : undefined}>
              <span
                className="career-dot"
                style={{ background: `#${clubs[r.team].kit.primary.toString(16).padStart(6, "0")}` }}
              />
              {r.name} · {POSITIONS[r.role].label}
              {r.id === conn.you ? " (você)" : ""}
            </li>
          ))}
        </ul>
      </aside>
      {hud && <PilotPanel hud={hud} name={pilot.name} role={role} mode="medio" />}
      <ControlsHelp />
      {state === "erro" && (
        <p className="stage-message" role="alert">
          Não foi possível mostrar a partida em 3D neste navegador.
        </p>
      )}
    </div>
  );
}

/** Ida e volta, última correção e comandos à frente do servidor (item 6.2). */
function NetLine({ conn }: { conn: Connection }) {
  const n = conn.net();
  return (
    <p className="online-net" data-testid="rede">
      Rede: {n.ping === null ? "medindo…" : `${Math.round(n.ping)} ms de ida e volta`} · correção{" "}
      {Math.round(n.correction * 100)} cm · {n.pending} à frente
      {n.simulated > 0 && <span> (latência simulada: {n.simulated} ms)</span>}
    </p>
  );
}
